import { ThemeProvider } from 'styled-components';
import theme from '../../global/theme';
import { GlobalStyle } from '../../global/global';
import './styles.css';
import {
    Box,
    Text,
    Heading,
    List,
    ListItem,
    ListIcon,
    Link,
    Flex,
    Select,
    Image,
    Grid,
    Show,
} from '@chakra-ui/react';
import { useState, useId } from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import StyledButton from '../../components/Button/Button';
import { CheckIcon } from '@chakra-ui/icons';
import ContactSection from '../../sections/ContactSection/ContactSection';

interface IntegrationData {
    title: string;
    description: string;
    items: Array<{
        broker: string;
        imageSrc: string;
        title: string;
        description: string;
        href: string;
        buttonText: string;
    }>;
}
interface FeatureTileProps {
    title: string;
    content: string;
}

const brokerData: IntegrationData = {
    title: 'Daj swoim klientom wybór',
    description:
        'Oferuj najlepszych kurierów i różnorodne formy dostawy dla swoich klientów. Dzięki IdoSell Broker nie musisz podpisywać indywidualnych umów z każdym z nich.',
    items: [
        {
            broker: 'Smile',
            imageSrc: 'https://www.idosell.com/panel/file-gate.php?id=611379&ext=.svg',
            title: 'Darmowa dostawa Smile',
            description:
                'Oferuj klientom darmową dostawę do Paczkomatów i punktów odbioru. Z usługą Smile zmotywujesz ich do zakupu, zyskasz nowe możliwości sprzedażowe i zwiększysz swoją konwersję.',
            href: 'https://www.idosell.com/pl/smile-darmowa-dostawa-w-sklepach-internetowych-od-idosell/',
            buttonText: 'Włącz dostawę',
        },
        {
            broker: 'InPost',
            imageSrc: 'https://www.idosell.com/panel/file-gate.php?id=611386&ext=.svg',
            title: 'Paczkomat® i Kurier InPost ',
            description:
                'Daj swoim klientom pełną elastyczność w wyborze formy dostawy dzięki integracji z InPost. Paczkomaty to idealne rozwiązanie dla osób ceniących sobie samodzielny odbiór o dowolnej porze – również w weekendy. Z kolei usługa InPost Kurier umożliwia dostawę prosto pod drzwi, z opcją wyboru dogodnych godzin doręczenia. Jako sprzedawca możesz wygodnie nadawać paczki z magazynu, Paczkomatu, punktu POP lub oddziału InPost.',
            href: 'https://www.idosell.com/pl/inpost-przesylki-kurierskie/',
            buttonText: 'Włącz dostawę',
        },
        {
            broker: 'ORLEN Paczka',
            imageSrc: 'https://www.idosell.com/panel/file-gate.php?id=611384&ext=.svg',
            title: 'ORLEN Paczka',
            description:
                'ORLEN Paczka oferuje wygodne i błyskawiczne dostawy w ciągu 1 dnia. Dzięki współpracy z IdoSell, integracja ORLEN Paczki jest prosta i nie wymaga podpisywania dodatkowych umów.',
            href: 'https://www.idosell.com/pl/integracje-gotowe-do-wlaczenia-jednym-kliknieciem-/integracja-z-kurierami-w-sklepie-internetowym/szybka-i-wygodna-usluga-dostawy-i-odbioru-paczek-z-orlen-paczka/',
            buttonText: 'Włącz dostawę',
        },
        {
            broker: 'DPD',
            imageSrc: 'https://www.idosell.com/panel/file-gate.php?id=611385&ext=.svg',
            title: 'DPD',
            description:
                'Dzięki współpracy z DPD Polska, merchanci IdoSell otrzymują korzystne warunki współpracy z kurierami oraz dostęp do automatów paczkowych i punktów odbioru. Integracja jest prosta, a ceny są transparentne i atrakcyjne. DPD oferuje również międzynarodową dostawę, umożliwiając wysyłkę paczek do wielu krajów, dzięki czemu możesz rozwijać sprzedaż na rynkach zagranicznych.',
            href: 'https://www.idosell.com/pl/idosell-broker-czyli-tanie-uslugi-kurierskie-bez-podpisywania-umowy/tanie-przesylki-krajowe-i-miedzynarodowe-bez-umowy-i-deklaracji-ilosci-dzieki-wspolpracy-iai-i-dpd-polska/',
            buttonText: 'Włącz dostawę',
        },
        {
            broker: 'Zwroty do Paczkomatów® InPost',
            imageSrc: 'https://www.idosell.com/panel/file-gate.php?id=611386&ext=.svg',
            title: 'Zwroty do Paczkomatów® InPost',
            description:
                'Oferuj usługę łatwych i wygodnych zwrotów dzięki współpracy z InPost Paczkomaty. To rozwiązanie buduje zaufanie i zwiększa lojalność klientów.',
            href: 'https://www.idosell.com/pl/obsluga-zwrotow-towarow/',
            buttonText: 'Włącz dostawę',
        },
    ],
};

const FeatureTile: React.FC<FeatureTileProps> = ({ title, content }) => (
    <Box
        bg="#eee"
        p={{ base: '35px 30px', lg: '30px' }}
        display="flex"
        flexDirection="column"
        gap="15px"
        height="100%"
        minHeight={{ base: '260px', sm: '350px' }}
    >
        <Heading
            fontSize={{ base: '21px', lg: '28px' }}
            fontWeight="600"
            lineHeight="1.2"
            position="relative"
            pb="30px"
            _after={{
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '0',
                width: '30px',
                height: '3px',
                backgroundColor: '#171717',
            }}
        >
            {title}
        </Heading>
        <Text fontSize={{ base: '15px', lg: '16px' }} lineHeight="1.6">
            {content}
        </Text>
    </Box>
);

const BrokerSection = () => {
    const [selectedBroker, setSelectedBroker] = useState(0);
    const uniqueId = useId();
    const selectId = `broker-select-${uniqueId}`;
    const [isLargerThan768] = useMediaQuery('(min-width: 768px)');

    return (
        <Box>
            {/* Mobile Select */}
            <Box display={{ base: 'block', md: 'none' }} px="15px">
                <Text
                    as={'label'}
                    htmlFor={selectId}
                    fontSize={'12px'}
                    marginBottom={'5px'}
                    display={'block'}
                >
                    Forma dostawy:
                </Text>
                <Select
                    id={selectId}
                    value={selectedBroker}
                    onChange={e => setSelectedBroker(Number(e.target.value))}
                    borderRadius="none"
                    borderWidth="0"
                    outlineColor="gray.400"
                    outlineOffset="0"
                    _placeholder={{ color: 'gray.400' }}
                    _focusVisible={{
                        outlineWidth: '2px',
                        outlineStyle: 'solid',
                        outlineColor: 'gray.800',
                        borderColor: 'gray.800',
                        boxShadow: 'none',
                    }}
                    sx={{
                        '& option': {
                            background: 'gray.400',
                            color: 'gray.0',
                        },
                    }}
                >
                    {brokerData.items.map((item, index) => (
                        <option key={index} value={index}>
                            {item.broker}
                        </option>
                    ))}
                </Select>
            </Box>

            {/* Content */}
            <Box mt={{ base: '20px', lg: '50px' }} mb={{ base: '0', sm: '50px', lg: '0' }}>
                {isLargerThan768 ? (
                    // Desktop view - all boxes
                    brokerData.items.map((item, index) => (
                        <Grid
                            key={index}
                            mb={index !== brokerData.items.length - 1 ? '30px' : '0'}
                            mt="60px"
                            p="30px"
                            bg="white"
                            gap="50px"
                            gridTemplateColumns={{ base: '1fr', md: '1fr 4fr' }}
                        >
                            <Flex justifyContent="center" alignItems="center">
                                <Image
                                    src={item.imageSrc}
                                    alt={item.title}
                                    mb="20px"
                                    objectFit="contain"
                                    maxW="180px"
                                />
                            </Flex>
                            <Flex direction="column" justifyContent="center" alignItems="start">
                                <Heading
                                    as="h3"
                                    fontSize="26px"
                                    lineHeight="34px"
                                    mb="14px"
                                    fontWeight="600"
                                >
                                    {item.title}
                                </Heading>
                                <Text mb="40px">{item.description}</Text>
                                <StyledButton href={item.href} style="default" target="_blank">
                                    {item.buttonText}
                                </StyledButton>
                            </Flex>
                        </Grid>
                    ))
                ) : (
                    // Mobile view - selected box only
                    <Box p="30px" bg="white">
                        <Image
                            src={brokerData.items[selectedBroker].imageSrc}
                            alt={brokerData.items[selectedBroker].title}
                            objectFit="contain"
                            maxW="170px"
                            mt="30px"
                            mb="60px"
                            mx="auto"
                        />
                        <Heading
                            as="h3"
                            fontSize="30px"
                            lineHeight="37px"
                            fontWeight="600"
                            mb="15px"
                        >
                            {brokerData.items[selectedBroker].title}
                        </Heading>
                        <Text mb="50px">{brokerData.items[selectedBroker].description}</Text>
                        <StyledButton
                            href={brokerData.items[selectedBroker].href}
                            style="default"
                            target="_blank"
                        >
                            {brokerData.items[selectedBroker].buttonText}
                        </StyledButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const BrokerInfoSection = () => {
    const [isLargerThan979] = useMediaQuery('(min-width: 979px)');

    return isLargerThan979 ? (
        // Desktop version (current one)
        <Box
            position="relative"
            py={{ base: '40px', lg: '80px' }}
            id="broker__info"
            scrollMarginTop={{ base: '55px', lg: '80px' }}
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100vw',
                height: '100%',
                backgroundImage: `url('https://www.idosell.com/panel/file-gate.php?id=610801&ext=.webp')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: -1,
            }}
        >
            <Flex
                maxW="1200px"
                mx="auto"
                direction={{ base: 'column', lg: 'row' }}
                position="relative"
                minH={{ lg: '500px' }}
                gap={{ base: 0, lg: '50px' }}
            >
                {/* Left Section */}
                <Box
                    flex="1"
                    position="relative"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Box
                        position="relative"
                        px={{ base: '15px', lg: 0 }}
                        py={{ base: '35px', lg: '60px' }}
                    >
                        <Heading
                            as="h2"
                            fontWeight="600"
                            fontSize={{ base: '30px', lg: '34px' }}
                            lineHeight={{ base: '37px', lg: '48px' }}
                            marginBottom="15px"
                            color="white"
                        >
                            IdoSell Broker
                        </Heading>
                        <Text fontSize={{ base: '16px', lg: '18px' }} color="white" mb="15px">
                            Skorzystaj z wyjątkowego rozwiązania i oferuj swoim klientom najlepszych
                            kurierów bez konieczności podpisywania indywidualnych umów z nimi.
                        </Text>
                        <Text fontSize={{ base: '16px', lg: '18px' }} color="white" mb="30px">
                            Z IdoSell Broker zyskasz pełną kontrolę nad logistyką i wygodę
                            zarządzania z jednego panelu.
                        </Text>

                        <Box
                            display="flex"
                            flexDirection={{ base: 'column', md: 'row' }}
                            alignItems={{ base: 'stretch', md: 'center' }}
                            gap="30px"
                        >
                            <StyledButton
                                href="https://pomoc.idosell.com/sprzedaz/kurierzy-i-generowanie-przesylek/konfiguracja-kurierow-informacja-ogolne"
                                style="outline-reverted"
                                target="_blank"
                            >
                                Skonfiguruj teraz
                            </StyledButton>
                        </Box>
                    </Box>
                </Box>

                {/* Right Section */}
                <Box
                    display={{ base: 'none', lg: 'flex' }}
                    flex="1"
                    flexDirection="column"
                    justifyContent="center"
                    bg="#1AAC7A"
                    py="60px"
                    px="50px"
                    maxW="571px"
                    width="100%"
                >
                    <Text
                        fontSize="26px"
                        lineHeight="34px"
                        color="white"
                        textAlign="center"
                        mb="30px"
                        fontWeight="700"
                    >
                        Spełnij potrzeby swoich klientów
                    </Text>
                    <List spacing="15px" fontSize="18px" lineHeight="27px" color="#fff">
                        {[
                            'Brak formalności - kurierzy bez negocjacji i zobowiązań',
                            'Atrakcyjne stawki - niskie ceny, które są nieosiągalne na umowach własnych',
                            'Pełna przejrzystość - widzisz wszystkie szczegóły opłat i składników ceny',
                            'Natychmiastowy start - wybranego kuriera uruchomisz w klika chwil',
                            'Szybkie i wygodne zwroty z InPost Paczkomaty i Smile',
                            'Atrakcyjne warunki dla przesyłek międzynarodowych',
                            'Jedna faktura i wygodne zarządzanie opłatami w panelu IdoSell',
                        ].map((text, index) => (
                            <ListItem key={index} display="flex" alignItems="center">
                                <ListIcon as={CheckIcon} mr="30px" color="#171717" />
                                <Box>
                                    <Text fontSize="16px" lineHeight="1.5">
                                        {text}
                                    </Text>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Flex>
        </Box>
    ) : (
        // Mobile/Tablet version
        <Box
            position="relative"
            py={{ base: '40px', md: '60px' }}
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100vw',
                height: '100%',
                backgroundImage: `url('https://www.idosell.com/panel/file-gate.php?id=610801&ext=.webp')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: -1,
            }}
        >
            <Box maxW="1200px" mx="auto" position="relative" color="white">
                <Text
                    color="#1AAC7A"
                    fontSize="14px"
                    fontWeight="600"
                    mb="15px"
                    textTransform="uppercase"
                    display="inline-block"
                    padding="1px 4px 0px"
                    border="2px solid #1AAC7A"
                >
                    POLECAMY TAKŻE
                </Text>
                <Heading
                    as="h2"
                    fontSize={{ base: '30px', md: '34px' }}
                    lineHeight="1.2"
                    fontWeight="600"
                    mb="20px"
                >
                    Gotowy na wygodne i tanie przesyłki?
                </Heading>
                <Text fontSize={{ base: '16px', md: '18px' }} lineHeight="1.5" mb="30px">
                    Uruchom IdoSell Broker w swoim sklepie internetowym i zyskaj pełną kontrolę nad
                    logistyką bez podpisywania umów.
                </Text>

                <Heading
                    as="h3"
                    fontSize={{ base: '24px', md: '28px' }}
                    lineHeight="1.4"
                    fontWeight="600"
                    mb="20px"
                >
                    Zyskaj więcej dzięki IdoSell Broker
                </Heading>

                <List spacing="15px" mb="70px">
                    {[
                        'Brak formalności',
                        'Atrakcyjne stawki',
                        'Pełna przejrzystość',
                        'Natychmiastowy start',
                        'Wygodne zwroty',
                        'Wysyłka za granicę',
                        'Jedna faktura',
                        'Analityka',
                    ].map((item, index) => (
                        <ListItem key={index} display="flex" alignItems="center">
                            <ListIcon as={CheckIcon} color="#1AAC7A" mr="15px" />
                            <Text fontSize="16px">{item}</Text>
                        </ListItem>
                    ))}
                </List>

                <Box display="flex" flexDirection="column" gap="15px">
                    <StyledButton
                        href="https://www.idosell.com/pl/dashboard/?ref=http%3A%2F%2Fwww.idosell.com%2Foauth%2Fauthorize%2F%3Fresponse_type%3Dcode%26client_id%3Dcsc_shop%26redirect_uri%3Dhttps%3A%2F%2Fcsc.idosell.com%2Fapi%2F%26scope%3Dopenid%2520csc%26state%3DLtoIdkBz5ILUbDZ9ltck-pgZscFinY2nr-9x3lZrDZzbM08q3oH6c0aCQpxF8z6xVys-"
                        style="outline-reverted"
                        target="_blank"
                    >
                        Włącz IdoSell Broker
                    </StyledButton>
                    <Link
                        href="https://www.idosell.com/pl/idosell-broker-czyli-tanie-uslugi-kurierskie-bez-podpisywania-umowy/uruchomienie-uslugi-iai-broker/"
                        color="white"
                        fontWeight="600"
                        textDecoration="underline"
                        textAlign="center"
                        _hover={{ color: '#1AAC7A' }}
                    >
                        Dowiedz się więcej
                    </Link>
                </Box>
            </Box>
        </Box>
    );
};

export default function BrokerPage() {
    return (
        <ThemeProvider theme={theme}>
            <GlobalStyle $whiteColor />
            <Box id="broker__page">
                <Box
                    position="relative"
                    display="flex"
                    justifyContent="center"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        backgroundColor: '#1AAC7A',
                        width: '100vw',
                        height: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: -1,
                    }}
                >
                    <Flex
                        maxW="1200px"
                        direction={{ base: 'column', lg: 'row-reverse' }}
                        align="center"
                        w="100%"
                    >
                        <Box
                            flex={{ lg: '1 0 50%' }}
                            display="flex"
                            justifyContent="center"
                            bg="var(--color-green)"
                        >
                            <picture>
                                <source
                                    media="(min-width: 979px)"
                                    srcSet="https://www.idosell.com/panel/file-gate.php?id=611185&ext=.png"
                                />
                                <source
                                    media="(min-width: 757px)"
                                    srcSet="https://www.idosell.com/panel/file-gate.php?id=611185&ext=.png"
                                />
                                <Image
                                    src="https://www.idosell.com/panel/file-gate.php?id=611186&ext=.png"
                                    alt="Kurier z paczkami"
                                    w={{ base: '100vw', lg: 'auto' }}
                                    maxW={{ base: '100vw', lg: '100%' }}
                                    maxH={{ lg: '485px', xl: '585px' }}
                                />
                            </picture>
                        </Box>

                        <Flex
                            flex={{ lg: '1 0 50%' }}
                            direction="column"
                            alignItems={{ base: 'center', lg: 'start' }}
                            gap="15px"
                            p={{ base: '30px 20px', lg: '10px 50px 15px 15px' }}
                            bg="var(--color-green)"
                            color="white"
                        >
                            <Text
                                as="strong"
                                color="#171717"
                                fontSize={{ base: '40px', xl: '40px' }}
                                mb="0"
                                display={{ base: 'none', lg: 'block' }}
                            >
                                .broker
                            </Text>
                            <Heading
                                as="h1"
                                fontSize={{ base: '36px', lg: '36px', xl: '54px' }}
                                lineHeight={{ base: '42px', lg: '48px', xl: '69px' }}
                                textAlign={{ base: 'center', lg: 'left' }}
                                fontWeight="600"
                                m="0"
                            >
                                Tanie przesyłki bez podpisywania umowy
                            </Heading>

                            <Text
                                fontSize={{ base: '16px', lg: '18px' }}
                                lineHeight={{ base: '24px', lg: 'normal' }}
                                textAlign={{ base: 'center', lg: 'left' }}
                            >
                                Z IdoSell Broker zyskujesz dostęp do sprawdzonych usług kurierskich
                                bez konieczności podpisywania indywidualnych umów.
                            </Text>
                            <Flex
                                justifyContent={{ base: 'center', lg: 'start' }}
                                mt={{ base: '15px', lg: '0' }}
                            >
                                <StyledButton
                                    href={'#broker__info'}
                                    style={'default'}
                                    target="_self"
                                >
                                    Dowiedz się więcej
                                </StyledButton>
                            </Flex>
                        </Flex>
                    </Flex>
                </Box>

                <div className="homepage containerIdosellPages">
                    {/* Features Section */}
                    <Box p={{ base: '20px 0px 55px', lg: '50px 15px 80px' }}>
                        <Heading
                            textAlign="center"
                            fontSize={{ base: '30px', lg: '34px' }}
                            lineHeight={{ base: '1.2', lg: '48px' }}
                            fontWeight="600"
                            mb={{ base: '15px', lg: '20px' }}
                        >
                            Proste zasady, maksymalna wygoda
                        </Heading>
                        <Text fontSize="18px" lineHeight="1.5" textAlign="center">
                            IdoSell Broker to usługa, która daje Ci pełną kontrolę nad dostawami w
                            Twoim sklepie internetowym, łącząc efektywną logistykę z wygodnym
                            systemem rozliczeń.
                        </Text>
                        <Grid
                            templateColumns={{
                                base: '1fr',
                                sm: 'repeat(2, 1fr)',
                                lg: 'repeat(3, 1fr)',
                            }}
                            gap={{ base: '15px', lg: '30px' }}
                            maxW="1200px"
                            mx="auto"
                            mt={{ base: '30px', lg: '80px' }}
                            px={{ base: '0', lg: '0' }}
                        >
                            <FeatureTile
                                title="Zero formalności i błyskawiczny start"
                                content="Korzystaj z usług kurierskich bez negocjacji i zobowiązań. Wybranego kuriera uruchomisz w kilka chwil."
                            />
                            <FeatureTile
                                title="Korzystne ceny i jedna zbiorcza faktura"
                                content="Ciesz się niskimi cenami przesyłek, które są nieosiągalne na umowach własnych. Wygodnie zarządzaj opłatami w panelu IdoSell."
                            />
                            <FeatureTile
                                title="Szybkie przesyłki za granicę"
                                content="IdoSell Broker to atrakcyjne warunki także dla przesyłek zagranicznych. W panelu widzisz wszystkie szczegóły opłat i składników ceny."
                            />
                        </Grid>
                    </Box>

                    <Box
                        my={{
                            base: 16,
                            md: 20,
                            xl: 24,
                        }}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={{
                            base: 20,
                            md: 24,
                        }}
                        position={'relative'}
                    >
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={'50px'}
                            position={'relative'}
                        >
                            <Box>
                                <Box
                                    display={'flex'}
                                    gap={'30px'}
                                    alignItems={'center'}
                                    flexDir={{ base: 'column', lg: 'row' }}
                                >
                                    <Box
                                        width={{ base: '100%', lg: '50%' }}
                                        padding={'30px'}
                                        display={'flex'}
                                        flexDirection={'column'}
                                        alignItems={'flex-start'}
                                    >
                                        <Text
                                            fontSize={'34px'}
                                            lineHeight={'1.5'}
                                            fontWeight={'600'}
                                            color={'#1AAC7A'}
                                            mb="30px"
                                        >
                                            Atrakcyjne ceny usług kurierskich w IdoSell Broker
                                        </Text>
                                        <List
                                            spacing={'15px'}
                                            fontSize={'18px'}
                                            lineHeight={'27px'}
                                            color={'#171717'}
                                            marginBottom={'30px'}
                                        >
                                            <ListItem display={'flex'}>
                                                <Box mr={'30px'} mt={'5px'}>
                                                    <svg
                                                        width="20"
                                                        height="25"
                                                        viewBox="0 0 20 25"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M11.058 17.8922L11.0655 17.8996L8.94418 20.021L3.68945 14.7662L5.81077 12.6449L8.9367 15.7708L16.5083 8.19922L18.6296 10.3205L11.058 17.8922Z"
                                                            fill="#1AAC7A"
                                                        />
                                                    </svg>
                                                </Box>

                                                <Box flex={'1'}>
                                                    <Text
                                                        color={''}
                                                        fontSize={'21px'}
                                                        lineHeight={'1.5'}
                                                        fontWeight={'600'}
                                                        mb="5px"
                                                    >
                                                        Wysyłka już od 7,29 zł
                                                    </Text>
                                                    <Text
                                                        fontSize={'16px'}
                                                        lineHeight={'1.5'}
                                                        mb="5px"
                                                    >
                                                        Paczki krajowe nadasz już od{' '}
                                                        <Text as={'span'} fontWeight={'600'}>
                                                            7,29 zł
                                                        </Text>{' '}
                                                        do automatu paczkowego lub od{' '}
                                                        <Text as={'span'} fontWeight={'600'}>
                                                            14,99 zł
                                                        </Text>{' '}
                                                        kurierem pod drzwi klienta. A przesyłki
                                                        zagraniczne wyślesz już od{' '}
                                                        <Text as={'span'} fontWeight={'600'}>
                                                            5,49 euro
                                                        </Text>{' '}
                                                        – szybko, wygodnie i z pełnym śledzeniem
                                                        paczki.
                                                    </Text>
                                                </Box>
                                            </ListItem>
                                            <ListItem display={'flex'}>
                                                <Box mr={'30px'} mt={'5px'}>
                                                    <svg
                                                        width="20"
                                                        height="25"
                                                        viewBox="0 0 20 25"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M11.058 17.8922L11.0655 17.8996L8.94418 20.021L3.68945 14.7662L5.81077 12.6449L8.9367 15.7708L16.5083 8.19922L18.6296 10.3205L11.058 17.8922Z"
                                                            fill="#1AAC7A"
                                                        />
                                                    </svg>
                                                </Box>

                                                <Box flex={'1'}>
                                                    <Text
                                                        color={''}
                                                        fontSize={'21px'}
                                                        lineHeight={'1.5'}
                                                        fontWeight={'600'}
                                                        mb="5px"
                                                    >
                                                        Preferencyjne stawki bez formalności
                                                    </Text>
                                                    <Text
                                                        fontSize={'16px'}
                                                        lineHeight={'1.5'}
                                                        mb="5px"
                                                    >
                                                        Dzięki IdoSell Broker zyskujesz dostęp do
                                                        preferencyjnych stawek u sprawdzonych
                                                        przewoźników – bez indywidualnych umów,
                                                        wystarczy tylko jedna umowa z IdoSell.
                                                    </Text>
                                                </Box>
                                            </ListItem>
                                        </List>
                                        <Link
                                            href="https://www.idosell.com/pl/szczegolowy-cennik-idosell/"
                                            background={'#171717'}
                                            color={'white'}
                                            padding={'12px 30px'}
                                            fontSize={'16px'}
                                            fontWeight={'600'}
                                            display={'flex'}
                                            alignItems={'center'}
                                            gap={'10px'}
                                            _hover={{
                                                underline: 'none',
                                                background: 'rgba(11, 113, 83, 0.5)',
                                            }}
                                        >
                                            Sprawdź aktualny cennik
                                            <svg
                                                width="9"
                                                height="14"
                                                viewBox="0 0 9 14"
                                                fill="white"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" />
                                            </svg>
                                        </Link>
                                    </Box>
                                    <Box width={{ base: '100%', lg: '50%' }}>
                                        <Show above="lg">
                                            <Image
                                                src="https://www.idosell.com/panel/file-gate.php?id=618647&ext=.png"
                                                alt=""
                                            ></Image>
                                        </Show>
                                        <Show below="lg">
                                            <Image
                                                src="https://www.idosell.com/panel/file-gate.php?id=618647&ext=.png"
                                                alt=""
                                            ></Image>
                                        </Show>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* END CTA */}
                    <Box
                        bg="#1AAC7A"
                        position="relative"
                        py="35px"
                        mb={{ base: '80px', lg: '100px' }}
                        display={{ base: 'block', md: 'none' }}
                    >
                        <Box maxW="1200px" mx="15px" px="15px">
                            <Heading
                                as="h2"
                                color="white"
                                fontSize="30px"
                                lineHeight="1.2"
                                fontWeight="600"
                                mb="15px"
                            >
                                Atrakcyjne ceny!
                            </Heading>
                            <Text color="#000" fontSize="12px" lineHeight="1.3" mb="30px">
                                Sprawdź aktualny cennik IdoSell Broker dla swojego sklepu
                                internetowego i korzystaj z atrakcyjnych stawek przesyłek w Polsce i
                                za granicą.
                            </Text>
                            <StyledButton href="#" style="default" target="_blank">
                                Sprawdź aktualny cennik
                            </StyledButton>
                        </Box>
                    </Box>

                    <Box paddingBottom={{ base: '5px', lg: '150px' }}>
                        <Text
                            fontSize="34px"
                            lineHeight="48px"
                            textAlign="center"
                            fontWeight="600"
                            mb="10px"
                            display={{ base: 'none', md: 'block' }}
                        >
                            Daj swoim klientom wybór
                        </Text>
                        <Text
                            fontSize="18px"
                            lineHeight="1.5"
                            textAlign="center"
                            display={{ base: 'none', md: 'block' }}
                        >
                            Oferuj najlepszych kurierów i różnorodne formy dostawy dla swoich
                            klientów. Dzięki IdoSell Broker nie musisz podpisywać indywidualnych
                            umów z każdym z nich.
                        </Text>
                        <BrokerSection />
                    </Box>

                    <Box
                        p={{ base: '50px 0', lg: '50px 0 112.5px 0' }}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={'50px'}
                        position={'relative'}
                    >
                        <Box>
                            <Box
                                display={'flex'}
                                gap={'30px'}
                                alignItems={'center'}
                                flexDir={{ base: 'column-reverse', lg: 'row' }}
                            >
                                <Box
                                    display={'flex'}
                                    justifyContent={'start'}
                                    alignItems={'start'}
                                    flexDir={'column'}
                                    width={{ base: '100%', lg: '50%' }}
                                >
                                    <Text
                                        fontSize={'34px'}
                                        lineHeight={'1.5'}
                                        fontWeight={'600'}
                                        color={'#171717'}
                                        mb="15px"
                                    >
                                        InPost International w IdoSell – wygodna wysyłka za granicę
                                    </Text>
                                    <Text color={'#171717'} fontSize={'18px'} marginBottom={'30px'}>
                                        Rozszerz zasięg swojego e-commerce dzięki nowej usłudze
                                        InPost International, która jest dostępna dla merchantów
                                        IdoSell.
                                    </Text>
                                    <Text color={'#171717'} fontSize={'18px'} marginBottom={'45px'}>
                                        Teraz możesz łatwo i szybko wysyłać paczki z Polski do
                                        prawie 70 000 punktów odbioru w Europie, w tym do
                                        Paczkomatów® i PaczkoPunktów. Usługa obejmuje takie kraje
                                        jak Włochy, Francja, Belgia, Holandia, Luksemburg, Hiszpania
                                        i Portugalia.
                                    </Text>
                                    <Link
                                        href="https://pomoc.idosell.com/sprzedaz/kurierzy-i-generowanie-przesylek/paczkomaty-inpost#Konfiguracja-kuriera-InPost-International"
                                        target="_blank"
                                        background={'#171717'}
                                        color={'white'}
                                        padding={'12px 30px'}
                                        fontSize={'16px'}
                                        fontWeight={'600'}
                                        display={'flex'}
                                        alignItems={'center'}
                                        gap={'10px'}
                                        _hover={{
                                            underline: 'none',
                                            background: 'rgba(11, 113, 83, 0.5)',
                                        }}
                                    >
                                        Dowiedz się więcej
                                        <svg
                                            width="9"
                                            height="14"
                                            viewBox="0 0 9 14"
                                            fill="white"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M0 13.6H2.70588L8.4 6.98565L2.70588 0.400024H0L5.69412 6.98565L0 13.6Z" />
                                        </svg>
                                    </Link>
                                </Box>
                                <Box
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    width={{ base: '100%', lg: '50%' }}
                                    display={'flex'}
                                >
                                    <Show above="lg">
                                        <Image
                                            src="https://www.idosell.com/panel/file-gate.php?id=616832&ext=.png"
                                            alt=""
                                        ></Image>
                                    </Show>
                                    <Show below="lg">
                                        <Image
                                            src="https://www.idosell.com/panel/file-gate.php?id=616832&ext=.png"
                                            alt=""
                                        ></Image>
                                    </Show>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box px={{ base: '15px', lg: 0 }}>
                        <BrokerInfoSection />
                    </Box>

                    <Box
                        py={{
                            base: 12,
                            md: 20,
                            xl: 24,
                        }}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={'35px'}
                        position={'relative'}
                    >
                        <ContactSection />
                    </Box>
                </div>
            </Box>
        </ThemeProvider>
    );
}
